import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { pathFor } from '../routes/paths';

const ACCENT = '#67313a';
const LOWER_BAND_BG = '#fbf7f2';

const SectionContainer = styled.section`
  padding: 80px 0 0;
  background: #ffffff;
`;

const LowerBand = styled.div`
  background: ${LOWER_BAND_BG};
  padding: 100px 0;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 60px;
  display: grid;
  grid-template-columns: 1.3fr 1.7fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    padding: 0 40px;
  }
`;

const IntroWrapper = styled.div`
  max-width: 760px;
  margin: 0 auto 80px;
  text-align: center;
  padding: 0 20px;
`;

const IntroLogo = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  margin: 0 auto 18px;
  display: block;
`;

const IntroMeta = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 10px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #8a8a8a;
  margin-bottom: 8px;
`;

const IntroTitle = styled.h2`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(22px, 3.2vw, 30px);
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7a7a7a;
  margin-bottom: 10px;
`;

const IntroSub = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-style: italic;
  font-weight: 400;
  font-size: clamp(14px, 1.8vw, 17px);
  line-height: 1.6;
  color: #7a7a7a;
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  margin-left: 0;
  height: 460px;
  width: 360px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  justify-self: end;
  align-self: center;
  transform: translateX(22px);
  @media (max-width: 968px) {
    justify-self: center;
    transform: none;
    width: 100%;
    max-width: 360px;
    margin: 0 auto;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TextContent = styled(motion.div)`
  margin-right: 0;
  justify-self: center;
  align-self: center;
  max-width: 560px;
  text-align: left;
  transform: translateX(-12px);
  @media (max-width: 968px) {
    justify-self: center;
    transform: none;
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
  }
`;

const RightTitle = styled.h2`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(19px, 2.8vw, 26px);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${ACCENT};
  margin-bottom: 8px;
`;

const RightSub = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-style: italic;
  font-weight: 400;
  font-size: 13px;
  line-height: 1.6;
  color: #8a8a8a;
`;

const Divider = styled.div`
  width: 64px;
  height: 2px;
  background: #d8c9c2;
  margin: 18px 0 22px;
`;

const Subtitle = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #b8a398;
  margin-bottom: 8px;
`;

const Meta = styled.p`
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #7d7d7d;
  margin: -6px 0 20px;
`;

const Description = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 14px;
  line-height: 1.65;
  color: #6e6e6e;
  margin-bottom: 30px;
`;

const Button = styled(motion.button)`
  background: ${ACCENT};
  border: none;
  padding: 14px 35px;
  font-family: 'Montserrat', sans-serif;
  font-size: 11px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #4e1f28;
    transform: translateY(-2px);
  }
`;

const WeddingPhotographersSection = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });
  const { lang } = useLanguage();
  const navigate = useNavigate();

  return (
    <SectionContainer ref={ref}>
      <IntroWrapper
        id="discover"
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
      >
        <IntroLogo src="/images/BADGE Grand Bordeaux.webp" alt="LL Monogram" />
        <IntroMeta>FRANCE & EUROPE</IntroMeta>
        <IntroTitle>{lang === 'fr' ? 'PHOTOGRAPHE DE MARIAGE' : 'WEDDING PHOTOGRAPHER'}</IntroTitle>
        <IntroSub>
          {lang === 'fr' ? (
            <>Des images naturellement élégantes<br />empreintes d’émotion</>
          ) : (
            <>Effortlessly elegant images<br />infused with emotion</>
          )}
        </IntroSub>
        <div style={{ height: 18 }} />
        <Button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate(pathFor('experience', lang))}
        >
          {lang === 'fr' ? 'En savoir plus' : 'Learn More'}
        </Button>
      </IntroWrapper>
      <LowerBand>
      <ContentWrapper>
        <ImageContainer
          initial={{ opacity: 0, scale: 0.95 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8 }}
        >
          <StyledImage src="/images/WEDDING PHOTOGRAPHER FOR REFINED COUPLES.webp" alt="Wedding Photographers" />
        </ImageContainer>
        <TextContent
          initial={{ opacity: 0, x: 50 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <RightTitle>{lang === 'fr' ? 'POUR LES COUPLES RAFFINÉS' : 'FOR REFINED COUPLES'}</RightTitle>
          <RightSub>
            {lang === 'fr'
              ? <>qui valorisent l’émotion, l’élégance intemporelle, et un héritage porteur de sens</>
              : <>who value emotion, timeless elegance, and a meaningful heritage</>}
          </RightSub>
          <Divider />
          {/* Intro tagline moved to the centered block above */}
          <Description>
            {lang === 'fr'
              ? <>Mon intention est de révéler ce que vous ressentez vraiment, transformer les émotions et les liens invisibles en photographies intemporelles et gracieuses.</>
              : <>My purpose is to reveal what you truly feel, turning emotions and invisible bonds into timeless and graceful photographs.</>}
          </Description>
          <Description>
            {lang === 'fr'
              ? <>Je porte une attention particulière aux détails, aux textures et à l’atmosphère qui rendent votre journée unique, les capturant avec sensibilité et un regard éditorial.</>
              : <>I pay close attention to the details, the textures, the atmosphere that make your day unique, capturing them with sensitivity and an editorial eye.</>}
          </Description>
          <Description>
            {lang === 'fr'
              ? <>Chaque mariage devient une œuvre sur‑mesure, préservant non seulement l’apparence, mais aussi la sensation, pour des souvenirs rares et durables qui composent votre héritage.</>
              : <>Each wedding becomes a bespoke work of art, preserving not only how it looked, but how it felt, offering rare and enduring memories that shape your legacy</>}
          </Description>
        </TextContent>
      </ContentWrapper>
      </LowerBand>
    </SectionContainer>
  );
};

export default WeddingPhotographersSection;
